import React, { useState, useEffect, useRef } from 'react';
import { useTeamContext } from '../../context/TeamContext';
import { useChat } from '../../context/ChatContext';
import { useProject } from '../../context/ProjectContext';
import { useNotice } from '../../context/NoticeContext';
import { useDashboard } from '../../context/DashboardContext';
import {
  FaUserFriends,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEllipsisH,
  FaTimes,
  FaUserPlus,
  FaEnvelopeOpen,
  FaCheck,
  FaChevronDown,
} from 'react-icons/fa';
import videoCameraIcon from '../../assets/video_camera.png';
import Folder from '../../assets/folder.png';
import Repository from '../../assets/repository.png';
import Noticeboard from '../../assets/noticeboard.png';
import Chat from '../../assets/chat.png';
import { notify } from '../../components/CustomToast';
import teamService from '../../services/teamService';
import userService from '../../services/userService';
import MultiSelectUsers from './MultiSelectUsers';
import InviteMembersSelect from './InviteMembersSelect';
import ConfirmActionModal from '../Modals/ConfirmActionModal';
import DeleteTeamModal from './DeleteTeamModal';
import InviteMembersModal from './InviteMembersModal';
import InvitedMembersModal from './InvitedMembersModal';
import { sanitizeUrl } from '../../utils/url';
import './TeamsSection.css';
import TeamCardSkeleton from './TeamCardSkeleton';
import BackButton from '../Common/BackButton';
import { useI18n } from '../../i18n';
import TeamRepository from '../Teams/TeamRepository';

// Maps to .avatar-color-0 … .avatar-color-9 (same palette as the member modals)
const getAvatarColorClass = (str) =>
  `avatar-color-${str ? str.charCodeAt(0) % 10 : 0}`;

const TeamsSection = () => {
  const { t } = useI18n();
  const {
    teams,
    loading,
    initialized,
    fetchTeams,
    addTeam,
    updateTeam,
    removeTeam,
    page,
    hasMore,
    loadingMore,
  } = useTeamContext();
  const { openTeamChat } = useChat();
  const { projects } = useProject();
  const { setActiveView } = useDashboard();
  const { setActiveTeamId: setNoticeTeamId } = useNotice();

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    // Check if scrolled near the bottom (e.g., within 50px)
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      if (!loading && !loadingMore && hasMore) {
        fetchTeams(page + 1);
      }
    }
  };

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTeamForEdit, setSelectedTeamForEdit] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedTeamForInvite, setSelectedTeamForInvite] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    member_ids: [],
    member_role: 'member',
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    member_ids: [],
    member_role: 'member',
  });
  const [inviteData, setInviteData] = useState({
    user_ids: [],
    role: 'member',
  });
  const [errors, setErrors] = useState({});
  const [chatLoading, setChatLoading] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [inviting, setInviting] = useState(false);

  // Create modal users state
  const [createModalUsers, setCreateModalUsers] = useState([]);
  const [loadingCreateModalUsers, setLoadingCreateModalUsers] = useState(false);

  // Edit modal users & invites state
  const [editModalUsers, setEditModalUsers] = useState([]);
  const [loadingEditModalUsers, setLoadingEditModalUsers] = useState(false);
  const [editModalInvites, setEditModalInvites] = useState([]);
  const [loadingEditModalInvites, setLoadingEditModalInvites] = useState(false);
  const [editInviteActionId, setEditInviteActionId] = useState(null);
  const [editMemberActionId, setEditMemberActionId] = useState(null);

  // Member role dropdown (edit modal) state
  const [openMemberMenuId, setOpenMemberMenuId] = useState(null);
  const memberMenuRef = useRef(null);

  // Transfer ownership confirmation state
  const [memberPendingOwnership, setMemberPendingOwnership] = useState(null);
  const [confirmTransferLoading, setConfirmTransferLoading] = useState(false);
  const [confirmTransferError, setConfirmTransferError] = useState(null);

  // Invited Members modal state
  const [showInvitedModal, setShowInvitedModal] = useState(false);
  const [selectedTeamForInvited, setSelectedTeamForInvited] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [invitesMeta, setInvitesMeta] = useState(null);
  const [loadingMoreInvites, setLoadingMoreInvites] = useState(false);

  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [confirmRemoveLoading, setConfirmRemoveLoading] = useState(false);
  const [confirmRemoveError, setConfirmRemoveError] = useState(null);
  const [confirmRemoveSuccess, setConfirmRemoveSuccess] = useState(false);
  const [memberPendingRemoval, setMemberPendingRemoval] = useState(null);

  // Delete team confirmation modal state
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);
  const [confirmDeleteError, setConfirmDeleteError] = useState(null);
  const [confirmDeleteSuccess, setConfirmDeleteSuccess] = useState(false);
  const [teamPendingDelete, setTeamPendingDelete] = useState(null);

  // Repository view state
  const [repositoryTeam, setRepositoryTeam] = useState(null);

  // Dropdown state for team card menu
  const [activeDropdownTeamId, setActiveDropdownTeamId] = useState(null);
  const dropdownRef = useRef(null);

  // Fetch teams on mount if not already initialized
  useEffect(() => {
    if (!initialized && !loading) {
      fetchTeams(1);
    }
  }, [initialized, loading, fetchTeams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdownTeamId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close the member role dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        memberMenuRef.current &&
        !memberMenuRef.current.contains(event.target)
      ) {
        setOpenMemberMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch all system users when create modal opens
  useEffect(() => {
    const fetchUsersForCreate = async () => {
      if (!showCreateModal) return;

      try {
        setLoadingCreateModalUsers(true);
        const response = await userService.list();
        if (response.status) {
          setCreateModalUsers(response.data.users || []);
        }
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setCreateModalUsers([]);
      } finally {
        setLoadingCreateModalUsers(false);
      }
    };

    fetchUsersForCreate();
  }, [showCreateModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSubmitting(true);

    try {
      // Build payload with optional member_ids
      const payload = {
        name: formData.name,
        description: formData.description,
      };

      // Only include member_ids if users are selected
      if (formData.member_ids.length > 0) {
        payload.member_ids = formData.member_ids;
        payload.member_role = formData.member_role;
      }

      const response = await teamService.create(payload);

      if (response.status) {
        // CRITICAL: Update TeamContext from API response (no extra GET request)
        if (response.data?.team) {
          addTeam(response.data.team);
        }

        notify('success', t('pages.teams.created_success'));
        setFormData({
          name: '',
          description: '',
          member_ids: [],
          member_role: 'member',
        });
        setCreateModalUsers([]);
        setShowCreateModal(false);
      } else {
        notify(
          'error',
          response.message || t('common.errors.failed_create_team')
        );
      }
    } catch (err) {
      if (err.status === 422) {
        setErrors(err.errors);
        notify('error', t('common.validation.error'));
      } else {
        notify('error', err.message || t('common.errors.failed_create_team'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch fresh team data and available users when invite modal opens
  useEffect(() => {
    const fetchData = async () => {
      if (!showInviteModal || !selectedTeamForInvite) return;

      try {
        setLoadingUsers(true);

        // Fetch fresh team data AND available users in parallel
        const [teamResponse, usersResponse] = await Promise.all([
          teamService.show(selectedTeamForInvite.id),
          userService.list({ team_id: selectedTeamForInvite.id }),
        ]);

        // Update with fresh team data (includes current members)
        if (teamResponse.status && teamResponse.data?.team) {
          setSelectedTeamForInvite(teamResponse.data.team);
        }

        // Update available users list
        if (usersResponse.status) {
          setUsers(usersResponse.data.users || []);
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
        notify('error', t('common.errors.failed_load_details'));
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInviteModal, selectedTeamForInvite?.id]);

  const handleInviteClick = (e, team) => {
    e.stopPropagation();
    setSelectedTeamForInvite(team);
    setShowInviteModal(true);
  };

  // Fetch invites when the Invited Members modal opens
  useEffect(() => {
    const fetchInvites = async () => {
      if (!showInvitedModal || !selectedTeamForInvited) return;

      try {
        setLoadingInvites(true);
        const response = await teamService.listInvites(
          selectedTeamForInvited.id,
          { limit: 8, page: 1 }
        );
        if (response.status) {
          setInvites(response.data.invites || []);
          setInvitesMeta(response.data.meta || null);
        }
      } catch (err) {
        console.error('Failed to fetch invites:', err);
        notify('error', t('common.errors.failed_load_details'));
      } finally {
        setLoadingInvites(false);
      }
    };

    fetchInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInvitedModal, selectedTeamForInvited?.id]);

  const handleInvitedClick = (e, team) => {
    e.stopPropagation();
    setSelectedTeamForInvited(team);
    setShowInvitedModal(true);
  };

  const handleResendInvite = async (inviteId) => {
    try {
      const response = await teamService.resendInvite(
        selectedTeamForInvited.id,
        inviteId
      );
      if (response.status) {
        if (response.data?.invite) {
          setInvites((prev) =>
            prev.map((inv) =>
              inv.id === inviteId ? response.data.invite : inv
            )
          );
        }
        notify('success', t('pages.teams.invitation_resent'));
      }
    } catch (err) {
      notify('error', err.message || t('common.errors.failed_action'));
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      const response = await teamService.cancelInvite(
        selectedTeamForInvited.id,
        inviteId
      );
      if (response.status) {
        // Backend returns updated first page of invites — replace state entirely
        setInvites(response.data.invites || []);
        setInvitesMeta(response.data.meta || null);
        notify('success', t('pages.teams.invitation_cancelled'));
      }
    } catch (err) {
      notify('error', err.message || t('common.errors.failed_action'));
    }
  };

  const handleLoadMoreInvites = async () => {
    if (
      !invitesMeta ||
      invitesMeta.current_page >= invitesMeta.last_page ||
      loadingMoreInvites
    )
      return;

    try {
      setLoadingMoreInvites(true);
      const response = await teamService.listInvites(
        selectedTeamForInvited.id,
        {
          limit: 8,
          page: invitesMeta.current_page + 1,
        }
      );
      if (response.status) {
        setInvites((prev) => [...prev, ...(response.data.invites || [])]);
        setInvitesMeta(response.data.meta || null);
      }
    } catch (err) {
      console.error('Failed to load more invites:', err);
      notify('error', t('pages.teams.load_more_invites_error'));
    } finally {
      setLoadingMoreInvites(false);
    }
  };

  const handleEditClick = (e, team) => {
    e.stopPropagation();
    setSelectedTeamForEdit(team);
    setEditFormData({
      name: team.name || '',
      description: team.description || '',
      member_ids: [],
      member_role: 'member',
    });
    setEditErrors({});
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedTeamForEdit(null);
    setOpenMemberMenuId(null);
    setEditFormData({
      name: '',
      description: '',
      member_ids: [],
      member_role: 'member',
    });
    setEditErrors({});
    setEditModalUsers([]);
    setEditModalInvites([]);
  };

  // Fetch fresh team data, available users and invites when the edit modal opens
  useEffect(() => {
    if (!showEditModal || !selectedTeamForEdit?.id) return;

    let cancelled = false;
    const teamId = selectedTeamForEdit.id;

    const fetchEditModalData = async () => {
      try {
        setLoadingEditModalUsers(true);
        setLoadingEditModalInvites(true);

        const [teamResponse, usersResponse, invitesResponse] =
          await Promise.all([
            teamService.show(teamId),
            userService.list({ team_id: teamId }),
            teamService.listInvites(teamId, { limit: 50, page: 1 }),
          ]);

        if (cancelled) return;

        if (teamResponse.status && teamResponse.data?.team) {
          setSelectedTeamForEdit(teamResponse.data.team);
        }
        if (usersResponse.status) {
          setEditModalUsers(usersResponse.data.users || []);
        }
        if (invitesResponse.status) {
          setEditModalInvites(invitesResponse.data.invites || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch edit modal data:', err);
          notify('error', t('common.errors.failed_load_details'));
        }
      } finally {
        if (!cancelled) {
          setLoadingEditModalUsers(false);
          setLoadingEditModalInvites(false);
        }
      }
    };

    fetchEditModalData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEditModal, selectedTeamForEdit?.id]);

  // Refresh the available-users list in the edit modal (after remove/cancel
  // actions a user may become invitable again)
  const refreshEditModalUsers = async (teamId) => {
    try {
      const usersResponse = await userService.list({ team_id: teamId });
      if (usersResponse.status) {
        setEditModalUsers(usersResponse.data.users || []);
      }
    } catch (err) {
      console.error('Failed to refresh users:', err);
    }
  };

  const handleEditMemberRoleChange = async (memberId, newRole) => {
    try {
      setEditMemberActionId(memberId);
      const response = await teamService.changeRole(
        selectedTeamForEdit.id,
        memberId,
        { role: newRole }
      );
      if (response.status && response.data?.team) {
        updateTeam(response.data.team);
        setSelectedTeamForEdit(response.data.team);
        notify('success', t('pages.teams.notifications.role_updated'));
      }
    } catch (err) {
      notify('error', t('pages.teams.notifications.role_update_failed'));
    } finally {
      setEditMemberActionId(null);
    }
  };

  const handleMemberRoleOption = (member, role) => {
    setOpenMemberMenuId(null);

    if (role === 'owner') {
      // Becoming owner means transferring ownership — confirm first
      setConfirmTransferError(null);
      setMemberPendingOwnership(member);
    } else if ((member.role || 'member') !== 'member') {
      handleEditMemberRoleChange(member.id, 'member');
    }
  };

  const handleEditTransferOwnership = async () => {
    if (!memberPendingOwnership || !selectedTeamForEdit) return;

    setConfirmTransferLoading(true);
    setConfirmTransferError(null);

    try {
      const response = await teamService.transferOwnership(
        selectedTeamForEdit.id,
        { new_owner_id: memberPendingOwnership.id }
      );
      if (response.status && response.data?.team) {
        updateTeam(response.data.team);
        setSelectedTeamForEdit(response.data.team);
        notify(
          'success',
          t('pages.teams.notifications.ownership_transferred')
        );
        setMemberPendingOwnership(null);
      } else {
        setConfirmTransferError(
          response.message ||
            t('pages.teams.notifications.ownership_transfer_failed')
        );
      }
    } catch (err) {
      setConfirmTransferError(
        err.message ||
          t('pages.teams.notifications.ownership_transfer_failed')
      );
    } finally {
      setConfirmTransferLoading(false);
    }
  };

  const handleEditRemoveMember = async (member) => {
    try {
      setEditMemberActionId(member.id);
      const response = await teamService.removeMember(
        selectedTeamForEdit.id,
        member.id
      );
      if (response.status && response.data?.team) {
        updateTeam(response.data.team);
        setSelectedTeamForEdit(response.data.team);
        notify('success', t('pages.teams.member_removed_success'));
        await refreshEditModalUsers(selectedTeamForEdit.id);
      } else {
        notify(
          'error',
          response.message ||
            t('pages.teams.notifications.failed_remove_member')
        );
      }
    } catch (err) {
      notify(
        'error',
        err.message || t('pages.teams.notifications.failed_remove_member')
      );
    } finally {
      setEditMemberActionId(null);
    }
  };

  const handleEditResendInvite = async (inviteId) => {
    try {
      setEditInviteActionId(inviteId);
      const response = await teamService.resendInvite(
        selectedTeamForEdit.id,
        inviteId
      );
      if (response.status) {
        if (response.data?.invite) {
          setEditModalInvites((prev) =>
            prev.map((inv) =>
              inv.id === inviteId ? response.data.invite : inv
            )
          );
        }
        notify('success', t('pages.teams.invitation_resent'));
      }
    } catch (err) {
      notify('error', err.message || t('common.errors.failed_action'));
    } finally {
      setEditInviteActionId(null);
    }
  };

  const handleEditCancelInvite = async (inviteId) => {
    try {
      setEditInviteActionId(inviteId);
      const response = await teamService.cancelInvite(
        selectedTeamForEdit.id,
        inviteId
      );
      if (response.status) {
        // Re-fetch with the edit modal's page size (cancel returns limit=8)
        const invitesResponse = await teamService.listInvites(
          selectedTeamForEdit.id,
          { limit: 50, page: 1 }
        );
        if (invitesResponse.status) {
          setEditModalInvites(invitesResponse.data.invites || []);
        }
        notify('success', t('pages.teams.invitation_cancelled'));
        await refreshEditModalUsers(selectedTeamForEdit.id);
      }
    } catch (err) {
      notify('error', err.message || t('common.errors.failed_action'));
    } finally {
      setEditInviteActionId(null);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditErrors({});
    setEditSubmitting(true);

    try {
      const response = await teamService.update(selectedTeamForEdit.id, {
        name: editFormData.name,
        description: editFormData.description,
      });

      if (response.status && response.data?.team) {
        let updatedTeam = response.data.team;

        // Send invitations for any newly selected users (mirrors invite flow)
        if (editFormData.member_ids.length > 0) {
          const promises = editFormData.member_ids.map((userId) =>
            teamService.invite(selectedTeamForEdit.id, {
              user_id: userId,
              role: editFormData.member_role,
            })
          );

          const responses = await Promise.all(promises);

          // Last response carries the freshest team data after all invites
          const lastResponse = responses[responses.length - 1];
          if (lastResponse.status && lastResponse.data?.team) {
            updatedTeam = lastResponse.data.team;
          }

          notify(
            'success',
            editFormData.member_ids.length > 1
              ? t('pages.teams.notifications.invitations_sent')
              : t('pages.teams.notifications.invitation_sent')
          );
        }

        updateTeam(updatedTeam);
        notify('success', t('pages.teams.updated_success'));
        closeEditModal();
      } else {
        notify('error', response.message || t('common.errors.failed_update'));
      }
    } catch (err) {
      if (err.status === 422) {
        setEditErrors(err.errors);
        notify('error', t('common.validation.error'));
      } else {
        notify(
          'error',
          err.message || t('pages.teams.notifications.update_failed')
        );
      }
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setInviting(true);

    try {
      // Send invitations for each selected user
      const promises = inviteData.user_ids.map((userId) =>
        teamService.invite(selectedTeamForInvite.id, {
          user_id: userId,
          role: inviteData.role,
        })
      );

      const responses = await Promise.all(promises);

      // Get the last response's team data (most up-to-date after all invites)
      const lastResponse = responses[responses.length - 1];
      if (lastResponse.status && lastResponse.data?.team) {
        // Update context with fresh team data (no extra API call needed)
        updateTeam(lastResponse.data.team);
      }

      notify('success', t('pages.teams.invited_success'));

      // Reset form and close modal
      setInviteData({ user_ids: [], role: 'member' });
      setSelectedTeamForInvite(null);
      setUsers([]);
      setShowInviteModal(false);
    } catch (err) {
      if (err.status === 422 && err.errors) {
        setErrors(err.errors);
        notify('error', t('common.validation.error'));
      }
      // Other errors are handled by api.js interceptor
    } finally {
      setInviting(false);
    }
  };

  const openRemoveMemberConfirm = (member) => {
    setMemberPendingRemoval(member);
    setConfirmRemoveLoading(false);
    setConfirmRemoveError(null);
    setConfirmRemoveSuccess(false);
    setConfirmRemoveOpen(true);
  };

  const closeRemoveModal = () => {
    setConfirmRemoveOpen(false);
    setMemberPendingRemoval(null);
    setConfirmRemoveError(null);
    setConfirmRemoveSuccess(false);
  };

  const handleConfirmRemoveMember = async () => {
    if (!memberPendingRemoval || !selectedTeamForInvite) return;

    setConfirmRemoveLoading(true);
    setConfirmRemoveError(null);

    try {
      const response = await teamService.removeMember(
        selectedTeamForInvite.id,
        memberPendingRemoval.id
      );

      if (response.status) {
        if (!response.data?.team) {
          setConfirmRemoveError(
            t('pages.teams.notifications.failed_remove_member')
          );
          return;
        }

        // Update team data in context and local state
        updateTeam(response.data.team);
        setSelectedTeamForInvite(response.data.team);

        // Set success state instead of closing modal
        setConfirmRemoveSuccess(true);

        notify('success', t('pages.teams.member_removed_success'));
      } else {
        setConfirmRemoveError(
          response.message ||
            t('pages.teams.notifications.failed_remove_member')
        );
      }
    } catch (err) {
      const message =
        err.message || t('pages.teams.notifications.failed_remove_member');
      setConfirmRemoveError(message);
      notify('error', message);
    } finally {
      setConfirmRemoveLoading(false);
    }
  };

  // Delete team handlers
  const handleDeleteClick = (e, team) => {
    e.stopPropagation();
    setTeamPendingDelete(team);
    setConfirmDeleteLoading(false);
    setConfirmDeleteError(null);
    setConfirmDeleteSuccess(false);
    setConfirmDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    setConfirmDeleteOpen(false);
    setTeamPendingDelete(null);
    setConfirmDeleteError(null);
    setConfirmDeleteSuccess(false);
  };

  const handleConfirmDeleteTeam = async () => {
    if (!teamPendingDelete) return;

    setConfirmDeleteLoading(true);
    setConfirmDeleteError(null);

    try {
      const response = await teamService.remove(teamPendingDelete.id);

      if (response.status) {
        removeTeam(teamPendingDelete.id);
        setConfirmDeleteSuccess(true);
        notify('success', t('pages.teams.deleted_success'));
      } else {
        setConfirmDeleteError(
          response.message || t('common.errors.failed_delete')
        );
      }
    } catch (err) {
      const message = err.message || t('common.errors.failed_delete');
      setConfirmDeleteError(message);
      notify('error', message);
    } finally {
      setConfirmDeleteLoading(false);
    }
  };

  const getTeamMembers = (team) => {
    const members = team.members || [];
    return members.slice(0, 5);
  };

  const getTotalMembers = (team) => {
    return team.members_count || team.members?.length || 0;
  };

  const handleChatClick = async (e, team) => {
    e.stopPropagation();

    if (chatLoading) return; // Prevent double-clicks

    try {
      setChatLoading(true);
      const conversationId = await openTeamChat(team.id);

      if (conversationId) {
        // Navigate to Talk section
        setActiveView('talk', { returnTo: 'teams' });

        // Scroll to Talk section
        setTimeout(() => {
          const element = document.getElementById('talk');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Failed to open team chat:', error);
    } finally {
      setChatLoading(false);
    }
  };

  const handleMeetingClick = (e) => {
    e.stopPropagation();
    setActiveView('meeting', { returnTo: 'teams' });
  };

  const handleProjectsClick = (e) => {
    e.stopPropagation();
    setActiveView('projects', { returnTo: 'teams' });
  };

  const handleRepositoryClick = (e, team) => {
    e.stopPropagation();
    setRepositoryTeam(team);
  };

  const handleNoticeboardClick = (e, team) => {
    e.stopPropagation();

    // Set active team for noticeboard
    setNoticeTeamId(team.id);

    // Switch to standalone noticeboard view
    setActiveView('noticeboard');
  };

  if (loading && teams.length === 0) {
    return (
      <div className="teams teams-section-container news-feed-section-container">
        <div
          className="section-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '20px',
            paddingLeft: '40px',
            width: 'fit-content',
          }}
        >
          <h2 className="section-title" style={{ margin: 0 }}>
            <BackButton
              onClick={() => setActiveView('dashboard')}
              ariaLabel={t('navigation.back_to_dashboard')}
            />
            {t('pages.teams.title')}
          </h2>
        </div>
        <div className="teams-loading-grid">
          {[...Array(6)].map((_, i) => (
            <TeamCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (repositoryTeam) {
    let currentUserId = null;
    try { currentUserId = JSON.parse(localStorage.getItem('user') || '{}')?.id || null; } catch {}
    return (
      <TeamRepository
        team={repositoryTeam}
        currentUserId={currentUserId}
        onBack={() => setRepositoryTeam(null)}
      />
    );
  }

  return (
    <div className="teams teams-section-container">
      <div className="dashboard-section-header">
        <h1 className="dashboard-section-title">
          <BackButton
            onClick={() => setActiveView('dashboard')}
            ariaLabel={t('navigation.back_to_dashboard')}
          />
          {t('pages.teams.title')}
        </h1>
      </div>

      <div className="teams-grid" onScroll={handleScroll}>
        {/* Create New Team Card */}
        <div
          className="team-card create-card"
          onClick={() => setShowCreateModal(true)}
        >
          <div className="create-icon">
            <FaPlus />
          </div>
          <div className="create-label">{t('pages.teams.create_new')}</div>
        </div>

        {/* Team Cards */}
        {teams.map((team) => (
          <div
            key={team.id}
            className="team-card"
            onClick={() => setSelectedTeam(team)}
          >
            {/* Section 1: Header */}
            <div className="team-header">
              <div className="team-name" title={team.name}>
                {team.name.length > 20
                  ? `${team.name.substring(0, 20)}...`
                  : team.name}
              </div>

              {/* Only show menu icon if user is owner */}
              {team.permissions?.is_owner && (
                <div
                  className="team-menu-container"
                  ref={activeDropdownTeamId === team.id ? dropdownRef : null}
                >
                  <div
                    className="team-menu-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveDropdownTeamId(
                        activeDropdownTeamId === team.id ? null : team.id
                      );
                    }}
                    title={t('pages.teams.team_options')}
                  >
                    <FaEllipsisH />
                  </div>

                  {/* Dropdown Menu */}
                  {activeDropdownTeamId === team.id && (
                    <div className="team-dropdown-menu">
                      <div
                        className="team-dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdownTeamId(null);
                          handleEditClick(e, team);
                        }}
                      >
                        <FaEdit className="dropdown-icon" />
                        <span>{t('pages.teams.edit_team')}</span>
                      </div>
                      <div
                        className="team-dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdownTeamId(null);
                          handleInviteClick(e, team);
                        }}
                      >
                        <FaUserPlus className="dropdown-icon" />
                        <span>{t('pages.teams.current_members')}</span>
                      </div>
                      <div
                        className="team-dropdown-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdownTeamId(null);
                          handleInvitedClick(e, team);
                        }}
                      >
                        <FaEnvelopeOpen className="dropdown-icon" />
                        <span>{t('pages.teams.invited_members')}</span>
                      </div>
                      <div
                        className="team-dropdown-item team-dropdown-item-danger"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdownTeamId(null);
                          handleDeleteClick(e, team);
                        }}
                      >
                        <FaTrash className="dropdown-icon" />
                        <span>{t('pages.teams.delete_team')}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Section 2: Members */}
            <div className="team-members-section">
              <div className="members-label">
                {t('pages.teams.members_count', {
                  count: getTotalMembers(team),
                })}
              </div>
              <div className="team-members-avatars">
                {getTeamMembers(team).map((member, index) => (
                  <div key={index} className="member-avatar" title={member.name || ''}>
                    <div className="avatar-placeholder">
                      {member.name?.charAt(0) || 'U'}
                    </div>
                    {member.avatar && (
                      <img
                        src={sanitizeUrl(member.avatar)}
                        alt=""
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    )}
                  </div>
                ))}
                {getTotalMembers(team) > 5 && (
                  <div className="see-more-text">
                    {t('pages.teams.see_more', {
                      count: getTotalMembers(team) - 5,
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Actions */}
            <div className="team-actions-section">
              <div className="actions-label">{t('pages.teams.actions')}</div>
              <div className="actions-list">
                {/* Visual setup per requirement */}
                <div
                  className="action-item"
                  onClick={(e) => handleMeetingClick(e)}
                >
                  <div className="action-item-icon">
                    <img src={videoCameraIcon} alt="" height={35} width={35} />
                  </div>
                  <span>{t('pages.teams.meeting')}</span>
                </div>
                <div
                  className="action-item"
                  onClick={(e) => handleProjectsClick(e)}
                >
                  <div className="action-item-icon">
                    <img src={Folder} alt="" height={35} width={35} />
                  </div>
                  <span>{t('pages.teams.projects')}</span>
                </div>

                <div
                  className="action-item"
                  onClick={(e) => handleRepositoryClick(e, team)}
                >
                  <div className="action-item-icon">
                    <img src={Repository} alt="" height={35} width={35} />
                  </div>
                  <span>{t('pages.teams.repository')}</span>
                </div>
                <div
                  className="action-item"
                  onClick={(e) => handleNoticeboardClick(e, team)}
                >
                  <div className="action-item-icon">
                    <img src={Noticeboard} alt="" height={35} width={35} />
                  </div>
                  <span>{t('pages.teams.noticeboard')}</span>
                </div>
                <div
                  className={`action-item ${chatLoading ? 'action-item-loading' : ''}`}
                  onClick={(e) => handleChatClick(e, team)}
                >
                  <div className="action-item-icon">
                    <img src={Chat} alt="" height={35} width={35} />
                  </div>
                  <span>
                    {chatLoading
                      ? t('pages.teams.opening')
                      : t('pages.teams.chat')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {loadingMore && (
          <div
            className="teams-loading-more"
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '20px',
            }}
          >
            {t('pages.teams.loading_more')}
          </div>
        )}
      </div>

      {/* Create Team Modal */}
      {showCreateModal && (
        <div
          className="team-modal-overlay"
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="team-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="team-modal-header">
              <h2 className="team-modal-title">
                {t('pages.teams.form.create_title')}
              </h2>
              <button
                type="button"
                className="team-modal-close"
                onClick={() => setShowCreateModal(false)}
                aria-label={t('common.actions.close')}
              >
                <FaTimes />
              </button>
            </div>

            <div className="team-modal-body">
              <form id="create-team-form" onSubmit={handleSubmit} noValidate>
                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.form.name_label')}{' '}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (errors.name) {
                        setErrors({ ...errors, name: null });
                      }
                    }}
                    placeholder={t('pages.teams.form.name_placeholder')}
                    className={`team-form-input ${errors.name ? 'is-invalid' : ''}`}
                  />
                  {errors.name && (
                    <div className="team-form-error">{errors.name[0]}</div>
                  )}
                </div>

                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.form.description_label')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => {
                      setFormData({ ...formData, description: e.target.value });
                      if (errors.description) {
                        setErrors({ ...errors, description: null });
                      }
                    }}
                    rows="3"
                    placeholder={t('pages.teams.form.description_placeholder')}
                    className={`team-form-textarea ${errors.description ? 'is-invalid' : ''}`}
                  />
                  {errors.description && (
                    <div className="team-form-error">
                      {errors.description[0]}
                    </div>
                  )}
                </div>

                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.form.invite_label')}
                  </label>
                  <InviteMembersSelect
                    users={createModalUsers}
                    selectedUserIds={formData.member_ids}
                    onChange={(selectedIds) => {
                      setFormData({ ...formData, member_ids: selectedIds });
                      if (errors.member_ids) {
                        setErrors({ ...errors, member_ids: null });
                      }
                    }}
                    role={formData.member_role}
                    onRoleChange={(newRole) =>
                      setFormData({ ...formData, member_role: newRole })
                    }
                    error={errors.member_ids ? errors.member_ids[0] : null}
                    loading={loadingCreateModalUsers}
                    placeholder={t('pages.teams.form.invite_placeholder')}
                  />
                </div>
              </form>
            </div>

            <div className="team-modal-footer">
              <button
                type="button"
                className="team-modal-btn team-btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={submitting}
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="submit"
                form="create-team-form"
                className="team-modal-btn team-btn-primary"
                disabled={submitting}
              >
                {submitting
                  ? t('common.status.submitting')
                  : t('common.actions.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && selectedTeamForEdit && (
        <div
          className="team-modal-overlay"
          onClick={closeEditModal}
        >
          <div
            className="team-modal-container team-edit-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="team-modal-header">
              <h2 className="team-modal-title">
                {t('pages.teams.form.edit_title')}
              </h2>
              <button
                type="button"
                className="team-modal-close"
                onClick={closeEditModal}
                aria-label={t('common.actions.close')}
              >
                <FaTimes />
              </button>
            </div>

            <div className="team-modal-body">
              <form id="edit-team-form" onSubmit={handleEditSubmit} noValidate>
                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.form.name_label')}
                    <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        name: e.target.value,
                      });
                      if (editErrors.name) {
                        setEditErrors({ ...editErrors, name: null });
                      }
                    }}
                    placeholder={t('pages.teams.form.name_placeholder')}
                    className={`team-form-input ${editErrors.name ? 'is-invalid' : ''}`}
                  />
                  {editErrors.name && (
                    <div className="team-form-error">{editErrors.name[0]}</div>
                  )}
                </div>

                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.form.description_label')}
                  </label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => {
                      setEditFormData({
                        ...editFormData,
                        description: e.target.value,
                      });
                      if (editErrors.description) {
                        setEditErrors({ ...editErrors, description: null });
                      }
                    }}
                    rows="3"
                    placeholder={t('pages.teams.form.description_placeholder')}
                    className={`team-form-textarea ${editErrors.description ? 'is-invalid' : ''}`}
                  />
                  {editErrors.description && (
                    <div className="team-form-error">
                      {editErrors.description[0]}
                    </div>
                  )}
                </div>

                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.form.invite_label')}
                  </label>
                  <InviteMembersSelect
                    users={editModalUsers}
                    selectedUserIds={editFormData.member_ids}
                    onChange={(selectedIds) => {
                      setEditFormData({
                        ...editFormData,
                        member_ids: selectedIds,
                      });
                      if (editErrors.member_ids) {
                        setEditErrors({ ...editErrors, member_ids: null });
                      }
                    }}
                    role={editFormData.member_role}
                    onRoleChange={(newRole) =>
                      setEditFormData({
                        ...editFormData,
                        member_role: newRole,
                      })
                    }
                    error={
                      editErrors.member_ids ? editErrors.member_ids[0] : null
                    }
                    loading={loadingEditModalUsers}
                    placeholder={t('pages.teams.form.invite_placeholder')}
                  />
                </div>

                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.current_members')}
                  </label>
                  <div className="edit-members-list">
                    {(selectedTeamForEdit.members || []).map((member) => (
                      <div key={member.id} className="member-row">
                        <div className="edit-member-identity">
                          <div className="member-avatar">
                            <div
                              className={`avatar-placeholder ${getAvatarColorClass(member.name)}`}
                            >
                              {member.name?.charAt(0)?.toUpperCase() || 'U'}
                            </div>
                            {member.avatar && (
                              <img
                                src={sanitizeUrl(member.avatar)}
                                alt=""
                                onError={(e) =>
                                  (e.target.style.display = 'none')
                                }
                              />
                            )}
                          </div>
                          <div className="member-info">
                            <strong>{member.name}</strong>
                            <span className="member-email">
                              {member.email}
                            </span>
                          </div>
                        </div>
                        <div className="member-actions">
                          {member.role === 'owner' ? (
                            <span className="role-badge owner">
                              {t('pages.teams.labels.owner')}
                            </span>
                          ) : (
                            selectedTeamForEdit.permissions?.is_owner && (
                              <div
                                className="member-role-menu-container"
                                ref={
                                  openMemberMenuId === member.id
                                    ? memberMenuRef
                                    : null
                                }
                              >
                                <button
                                  type="button"
                                  className="member-role-trigger"
                                  onClick={() =>
                                    setOpenMemberMenuId(
                                      openMemberMenuId === member.id
                                        ? null
                                        : member.id
                                    )
                                  }
                                  disabled={editMemberActionId === member.id}
                                >
                                  {t('pages.teams.labels.member')}
                                  <FaChevronDown className="member-role-caret" />
                                </button>
                                {openMemberMenuId === member.id && (
                                  <div className="member-role-menu">
                                    {['owner', 'member'].map((roleOption) => (
                                      <button
                                        key={roleOption}
                                        type="button"
                                        className="member-role-option"
                                        onClick={() =>
                                          handleMemberRoleOption(
                                            member,
                                            roleOption
                                          )
                                        }
                                      >
                                        <span className="member-role-check">
                                          {(member.role || 'member') ===
                                            roleOption && <FaCheck />}
                                        </span>
                                        {t(
                                          `pages.teams.labels.${roleOption}`
                                        )}
                                      </button>
                                    ))}
                                    <div className="member-role-divider" />
                                    <button
                                      type="button"
                                      className="member-role-option danger"
                                      onClick={() => {
                                        setOpenMemberMenuId(null);
                                        handleEditRemoveMember(member);
                                      }}
                                    >
                                      <span className="member-role-check" />
                                      {t('pages.teams.labels.remove')}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="team-form-group">
                  <label className="team-form-label">
                    {t('pages.teams.invited_members')}
                  </label>
                  <div className="edit-members-list">
                    {loadingEditModalInvites ? (
                      <div className="edit-members-empty">
                        {t('common.status.loading')}
                      </div>
                    ) : editModalInvites.length === 0 ? (
                      <div className="edit-members-empty">
                        {t('pages.teams.no_invites_found')}
                      </div>
                    ) : (
                      editModalInvites.map((invite) => (
                        <div key={invite.id} className="invited-member-row">
                          <div className="edit-member-identity">
                            <div className="member-avatar">
                              <div
                                className={`avatar-placeholder ${getAvatarColorClass(invite.email)}`}
                              >
                                {invite.email?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            </div>
                            <div className="invited-member-details">
                              <div className="invited-member-email">
                                {invite.email}
                              </div>
                              <div className="invited-member-meta">
                                {t('pages.teams.labels.role')}:{' '}
                                {invite.role || t('pages.teams.labels.member')}
                              </div>
                            </div>
                          </div>
                          <div className="invited-member-actions">
                            <span
                              className={`invite-status-badge ${invite.status}`}
                            >
                              {t(`pages.teams.statuses.${invite.status}`)}
                            </span>
                            {invite.status !== 'accepted' && (
                              <>
                                <button
                                  type="button"
                                  className="invite-action-btn resend"
                                  onClick={() =>
                                    handleEditResendInvite(invite.id)
                                  }
                                  disabled={editInviteActionId === invite.id}
                                >
                                  {editInviteActionId === invite.id
                                    ? '...'
                                    : t('common.actions.resend')}
                                </button>
                                <button
                                  type="button"
                                  className="invite-action-btn cancel"
                                  onClick={() =>
                                    handleEditCancelInvite(invite.id)
                                  }
                                  disabled={editInviteActionId === invite.id}
                                >
                                  {t('common.actions.cancel')}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </form>
            </div>

            <div className="team-modal-footer">
              <button
                type="button"
                className="team-modal-btn team-btn-secondary"
                onClick={closeEditModal}
                disabled={editSubmitting}
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="submit"
                form="edit-team-form"
                className="team-modal-btn team-btn-primary"
                disabled={editSubmitting}
              >
                {editSubmitting
                  ? t('common.status.submitting')
                  : t('common.actions.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Members Modal */}
      <InviteMembersModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setSelectedTeamForInvite(null);
          setUsers([]);
        }}
        team={selectedTeamForInvite}
        availableUsers={users}
        loadingUsers={loadingUsers}
        inviting={inviting}
        onInvite={async (userIds, role) => {
          setErrors({});
          setInviting(true);
          try {
            // Send invitations for each selected user
            const promises = userIds.map((userId) =>
              teamService.invite(selectedTeamForInvite.id, {
                user_id: userId,
                role: role,
              })
            );

            const responses = await Promise.all(promises);

            // Get the last response's team data
            const lastResponse = responses[responses.length - 1];
            if (lastResponse.status && lastResponse.data?.team) {
              updateTeam(lastResponse.data.team);
              setSelectedTeamForInvite(lastResponse.data.team);
            }

            // Refresh available users list
            const usersResponse = await userService.list({
              team_id: selectedTeamForInvite.id,
            });
            if (usersResponse.status) {
              setUsers(usersResponse.data.users || []);
            }

            notify(
              'success',
              userIds.length > 1
                ? t('pages.teams.notifications.invitations_sent')
                : t('pages.teams.notifications.invitation_sent')
            );
          } catch (err) {
            if (err.status === 422 && err.errors) {
              notify('error', t('common.validation.error'));
            }
          } finally {
            setInviting(false);
          }
        }}
        onRoleChange={async (memberId, newRole) => {
          try {
            const response = await teamService.changeRole(
              selectedTeamForInvite.id,
              memberId,
              { role: newRole }
            );
            if (response.status && response.data?.team) {
              updateTeam(response.data.team);
              setSelectedTeamForInvite(response.data.team);
              notify('success', t('pages.teams.notifications.role_updated'));
            }
          } catch (err) {
            notify('error', t('pages.teams.notifications.role_update_failed'));
          }
        }}
      />

      {/* Invited Members Modal */}
      <InvitedMembersModal
        isOpen={showInvitedModal}
        onClose={() => {
          setShowInvitedModal(false);
          setSelectedTeamForInvited(null);
          setInvites([]);
          setInvitesMeta(null);
        }}
        team={selectedTeamForInvited}
        invites={invites}
        loading={loadingInvites}
        onResend={handleResendInvite}
        onCancel={handleCancelInvite}
        hasMore={
          invitesMeta ? invitesMeta.current_page < invitesMeta.last_page : false
        }
        loadingMore={loadingMoreInvites}
        onLoadMore={handleLoadMoreInvites}
      />

      {/* Helper Modals */}
      <ConfirmActionModal
        isOpen={confirmRemoveOpen}
        title={
          confirmRemoveSuccess
            ? t('pages.teams.remove_member.success_title')
            : t('pages.teams.remove_member.title')
        }
        description={
          memberPendingRemoval
            ? t('pages.teams.remove_member.confirm_desc', {
                name: memberPendingRemoval.name,
                team: selectedTeamForInvite?.name,
              })
            : t('pages.teams.remove_member.generic_desc')
        }
        confirmLabel={t('pages.teams.remove_member.confirm_btn')}
        cancelLabel={t('common.actions.cancel')}
        successLabel={t('common.actions.close')}
        loading={confirmRemoveLoading}
        success={confirmRemoveSuccess}
        error={confirmRemoveError}
        onConfirm={handleConfirmRemoveMember}
        onCancel={closeRemoveModal}
        confirmDisabled={!memberPendingRemoval || !selectedTeamForInvite}
        renderAfterSuccess={
          <div className="confirm-success-content">
            <div className="success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" fill="#10B981" />
                <path
                  d="M8 12l2.5 2.5L16 9"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <p
              className="success-message"
              dangerouslySetInnerHTML={{
                __html: t('pages.teams.remove_member.success_msg', {
                  name: memberPendingRemoval?.name,
                }),
              }}
            />
            <p
              className="updated-count"
              dangerouslySetInnerHTML={{
                __html: t('pages.teams.remove_member.updated_count', {
                  count: selectedTeamForInvite?.members?.length || 0,
                }),
              }}
            />
          </div>
        }
      />

      <ConfirmActionModal
        isOpen={!!memberPendingOwnership}
        title={t('pages.teams.transfer_ownership.title')}
        description={t('pages.teams.transfer_ownership.confirm_desc', {
          name: memberPendingOwnership?.name || '',
          team: selectedTeamForEdit?.name || '',
        })}
        confirmLabel={t('pages.teams.transfer_ownership.confirm_btn')}
        cancelLabel={t('common.actions.cancel')}
        loading={confirmTransferLoading}
        error={confirmTransferError}
        onConfirm={handleEditTransferOwnership}
        onCancel={() => {
          if (!confirmTransferLoading) {
            setMemberPendingOwnership(null);
            setConfirmTransferError(null);
          }
        }}
        confirmDisabled={!memberPendingOwnership || !selectedTeamForEdit}
      />

      <DeleteTeamModal
        isOpen={confirmDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDeleteTeam}
        team={teamPendingDelete}
        loading={confirmDeleteLoading}
      />
    </div>
  );
};

export default TeamsSection;
